import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { downloadFile, importFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Download, Upload, FileSpreadsheet, Trash2, GitMerge } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { code: "", name: "", phone: "", address: "", wilayah: "", price_code: "" };

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => setRows((await api.get("/customers")).data), []);
  useEffect(() => { load(); }, [load]);

  const fileRef = useRef(null);
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const r = await importFile("/customers/import", f);
      toast.success(`Import selesai: ${r.data.created} ditambah, ${r.data.skipped} dilewati`);
      if (r.data.errors?.length) toast.warning(r.data.errors.join("; "));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal import"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Hapus pelanggan ${r.name}?`)) return;
    try {
      await api.delete(`/customers/${r.customer_id}`);
      toast.success("Pelanggan dihapus");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const mergeDups = async () => {
    if (!window.confirm("Gabungkan pelanggan dengan nama yang sama? Invoice akan dialihkan ke record tertua, duplikat dihapus.")) return;
    try {
      const r = await api.post("/customers/merge-duplicates");
      if (r.data.merged === 0) toast.info("Tidak ada nama duplikat");
      else toast.success(`${r.data.merged} duplikat digabung ke record tertua`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menggabungkan"); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/customers/${editing.customer_id}`, form);
      else await api.post("/customers", form);
      toast.success("Pelanggan tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="page-customers" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Master Data</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Pelanggan</h1>
          <p className="text-slate-500 text-sm mt-1">Setiap pelanggan punya kode harga — invoice otomatis memakai harga sesuai kodenya.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFile("/customers/template", "template_pelanggan.xlsx")} data-testid="btn-template"><FileSpreadsheet className="h-4 w-4 mr-1" />Template</Button>
          <Button variant="outline" size="sm" onClick={() => downloadFile("/customers/export", "daftar_pelanggan.xlsx")} data-testid="btn-export"><Download className="h-4 w-4 mr-1" />Export</Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-import"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={doImport} data-testid="input-import" />
          <Dialog open={open} onOpenChange={setOpen}>
          <Button variant="outline" onClick={mergeDups} data-testid="btn-merge-customers"><GitMerge className="h-4 w-4 mr-1" />Gabungkan Duplikat</Button>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-customer" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Tambah Pelanggan</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Ubah Pelanggan" : "Tambah Pelanggan"}</DialogTitle><DialogDescription className="sr-only">Form data pelanggan</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editing} data-testid="input-cust-code" /></div>
              <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-cust-name" /></div>
              <div><Label>No. HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Kode Harga</Label><Input placeholder="cth: A, B, GROSIR" value={form.price_code} onChange={(e) => setForm({ ...form, price_code: e.target.value.toUpperCase() })} data-testid="input-price-code" /></div>
              <div><Label>Wilayah</Label><Input placeholder="cth: Jakarta Timur" value={form.wilayah || ""} onChange={(e) => setForm({ ...form, wilayah: e.target.value })} data-testid="input-cust-wilayah" /></div>
              <div className="col-span-2"><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-500">Kode harga harus cocok dengan kode di Master Barang → Harga Bertingkat. Kosongkan untuk memakai harga jual normal.</p>
            <DialogFooter><Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-customer">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Card>
        <table className="w-full text-sm" data-testid="table-customers">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="py-2 px-4">Kode</th><th>Nama</th><th>Telp</th><th>Wilayah</th><th>Kode Harga</th><th>Alamat</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.customer_id} className="border-b">
                <td className="py-2 px-4 font-mono text-xs">{r.code}</td>
                <td className="font-medium">{r.name}</td>
                <td>{r.phone}</td>
                <td className="text-slate-500">{r.wilayah || "-"}</td>
                <td>{r.price_code ? <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 font-mono" data-testid={`price-code-${r.code}`}>{r.price_code}</Badge> : <span className="text-slate-400 text-xs">Normal</span>}</td>
                <td className="text-slate-500">{r.address}</td>
                <td className="whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-cust-${r.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)} data-testid={`del-cust-${r.code}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Belum ada pelanggan</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
